import toast from "react-hot-toast";
import axiosInstance from "./axiosInstance";

const _doGetNoToast = async (url) => {
  try {
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    console.error("Error retrieving data:", error);
    return error.response.data;
  }
};

const _doGet = async (url, showToast = true) => {
  try {
    const response = await axiosInstance.get(url);

    if (!response.data || !response.data.returnData) {
      return [];
    }

    if (showToast && response.data.returnMessage) {
      toast.success(response.data.returnMessage);
    }
    return response.data.returnData;
  } catch (error) {
    console.error("Error retrieving data:", error);
    toast.error(error.response?.data?.returnMessage || "Error retrieving data");
    return [];
  }
};

const _doPost = async (url, data) => {
  try {
    const response = await axiosInstance.post(url, data);
    toast.success(response.data.returnMessage);
    return response.data;
  } catch (error) {
    console.error("Error retrieving data:", error);
    toast.error(error.response.data.returnMessage);
    return error.response.data;
  }
};

const _doPut = async (url, data) => {
  try {
    const response = await axiosInstance.put(url, data);
    toast.success(response.data.returnMessage);
    return response.data;
  } catch (error) {
    console.error("Error retrieving data:", error);
    toast.error(error.response.data.returnMessage);
    return error.response.data;
  }
};

const _doDelete = async (url, data) => {
  try {
    const response = await axiosInstance.delete(url, { data });
    toast.success(response.data.returnMessage);
    return response.data;
  } catch (error) {
    console.error("Error retrieving data:", error);
    toast.error(error.response.data.returnMessage);
    return error.response.data;
  }
};

const useApi = {
  moveCircuitToStructure: async (data) => {
    return await _doPut(`/api/circuit/moveCircuit/`, data);
  },
  getSiteOnlyById: async (siteId) => {
    return await _doGet(`/api/CAFM/getFlatSiteDataById/${siteId}`, false);
  },
  getSitesOnly: async () => {
    return await _doGet("/api/CAFM/sitesOnly", false);
  },
  getLatestTimestamp: async () => {
    return await _doGet("/api/site/latestTimestamp", false);
  },
  getStructuresOnly: async () => {
    return await _doGet("/api/CAFM/structuresOnly", false);
  },
  getCircuitsOnly: async () => {
    return await _doGet("/api/CAFM/circuitsOnly", false);
  },

  getDiversityGroupsOnly: async () => {
    return await _doGet("/api/CAFM/diversityGroupsOnly", false);
  },

  getDiversityGroupAssignmentsOnly: async () => {
    return await _doGet("/api/CAFM/diversityGroupAssignmentsOnly", false);
  },
  getAllCategories: async () => {
    return await _doGet(`/api/category/all`);
  },
  getAllHandoffTypes: async () => {
    return await _doGet(`/api/handoffType/all`);
  },
  getAllRedundancyTypes: async () => {
    return await _doGet(`/api/redundancyType/all`);
  },
  getAllScopes: async () => {
    return await _doGet(`/api/scope/all`);
  },
  getAllStatus: async () => {
    return await _doGet(`/api/status/all`);
  },
  getAllTypes: async () => {
    return await _doGet(`/api/type/all`);
  },
  getAllUses: async () => {
    return await _doGet(`/api/use/all`);
  },
  getFormData: async () => {
    return await _doGet(`/api/formReferenceData/all`, false);
  },
  sitesWithFormData: async () => {
    return await _doGet(`/api/CAFM/sitesWithFormData`);
  },
  getSiteById: async (siteId) => {
    return await _doGet(`/api/CAFM/sitesWith/${siteId}`);
  },
  getAllCarriers: async () => {
    return await _doGet(`/api/carriers/all`);
  },
  getAllContacts: async () => {
    return await _doGetNoToast(`/api/contacts/all`);
  },
  getContactsByCarrier: async (carrierId) => {
    return await _doGet(`/api/contacts/${carrierId}`);
  },
  getContactsByCarrierName: async (carrierName) => {
    return await _doGet(`/api/contactsbyName/${carrierName}`);
  },
  getAuditData: async () => {
    return await _doGet(`/api/cafm/getAudits`);
  },
  getReferenceAuditData: async () => {
    return await _doGet(`/api/audits/reference`);
  },
  getCarrierContactsAuditData: async () => {
    return await _doGet(`/api/audits/carriersContacts`);
  },
  getDiversityAuditData: async () => {
    return await _doGet(`/api/audits/diversity`);
  },
  getSiteStructureCircuitAuditData: async () => {
    return await _doGet(`/api/audits/siteStructureCircuit`);
  },
  updateDiversityGroups: async (diversityGroups) => {
    console.log("diversityGroups: ", diversityGroups);

    return await _doPost("/api/diversity/update", diversityGroups);
  },
  getAllCurrencies: async () => {
    return await _doGetNoToast(`/api/iso4217`);
  },
  getAllTimezones: async () => {
    return await _doGetNoToast(`/api/timezones`);
  },
  getAllLanguages: async () => {
    return await _doGetNoToast(`/api/languages`);
  },
  getAllStructures: async () => {
    return await _doGet(`/api/CAFM/getStructures`);
  },
  createNewEntry: async (entryType, entry) => {
    return await _doPost(`/api/${entryType}/new`, entry);
  },
  updateEnabled: async (entryType, entry) => {
    return await _doPut(`/api/${entryType}/update`, entry);
  },
  updateSiteNote: async (note) => {
    return await _doPut(`/api/CAFM/updateNote`, note);
  },
  updateSiteData: async (siteData) => {
    return await _doPut(`/api/CAFM/updateSite`, siteData);
  },
  removeCircuit: async (circuitId) => {
    return await _doDelete(`/api/circuit/delete/${circuitId}`);
  },
  updateCircuitNote: async (circuitId, circuitNote) => {
    return await _doPut(`/api/circuit/updateNote/${circuitId}`, circuitNote);
  },
  updateCarrier: async (carrier) => {
    return await _doPut(`/api/carriers/update`, carrier);
  },
  uploadCarrier: async (carrier) => {
    return await _doPut(`/api/carriers/upload`, carrier);
  },
  updateContact: async (contact, carrierId) => {
    return await _doPut(`/api/contacts/update?carrierId=${carrierId}`, contact);
  },
  uploadContact: async (contact) => {
    return await _doPut(`/api/contacts/upload`, contact);
  },
  createTempData: async (siteData) => {
    return await _doPost(`/api/CAFM/tempData`, siteData);
  },
  createTempStructure: async (siteData) => {
    return await _doPost(`/api/CAFM/tempStructure`, siteData);
  },
  createCarrier: async (carrier) => {
    return await _doPost(`/api/carriers/new`, carrier);
  },

  createContact: async (contact, carrierId) => {
    return await _doPost(`/api/contacts/new?carrierId=${carrierId}`, contact);
  },

  deleteContact: async (contact, carrierId) => {
    return await _doPut(`/api/contacts/delete?carrierId=${carrierId}`, contact);
  },
};

export default useApi;
